import { useMemo } from "react";

import { i18nFilter } from "app/filters/i18nFilter";
import { allTrackers } from "app/services/track/track";

import { BillingPeriod } from "../BillingPeriod/BillingPeriod";
import type { IBillingPeriodItem, IBillingCycle } from "../types";
import { PaymentPeriod } from "../types";
import { getPriceByCurrency } from "../utils";

import {
    Wrapper,
    Title,
    RadioContainer,
    PaymentSummaryContainer,
    SummaryLineContainer,
    DueTodayText,
    Delimiter,
} from "./BillingCycleStyled";

const TODAY_PRICE = 0;

const translate = i18nFilter();
export const BillingCycle = ({
    monthlyPackagePrice,
    yearlyPackagePrice,
    currency,
    onChange,
    selectedPeriod,
}: IBillingCycle) => {
    const { sign, code } = currency;

    const discountPercent = useMemo<number>(() => {
        const yearlyPrice = getPriceByCurrency(yearlyPackagePrice, code).Price / 12;
        const monthlyPrice = getPriceByCurrency(monthlyPackagePrice, code).Price;
        if (!yearlyPrice || !monthlyPrice) {
            return 0;
        }

        const ratio = yearlyPrice / monthlyPrice;
        return Math.floor((1 - ratio) * 100);
    }, [monthlyPackagePrice, yearlyPackagePrice]);

    const periods: Array<IBillingPeriodItem> = [
        {
            period: PaymentPeriod.ANNUAL,
            title: translate("pro.billing_cycle.yearly.title"),
            text: translate("pro.billing_cycle.yearly.text"),
            price: yearlyPackagePrice,
            tagText: translate("pro.billing_cycle.yearly.tag", {
                percent: discountPercent.toString(),
            }),
        },
        {
            period: PaymentPeriod.MONTHLY,
            title: translate("pro.billing_cycle.monthly.title"),
            text: translate("pro.billing_cycle.monthly.text"),
            price: monthlyPackagePrice,
        },
    ];

    const changeBillingPeriod = (item: IBillingPeriodItem): void => {
        allTrackers.trackEvent("Package", "click", `billing cycle ${item.period}`);
        onChange(item.period);
    };

    return (
        <Wrapper>
            <Title>{translate("pro.billing_cycle.title")}</Title>
            {periods.length ? (
                <RadioContainer>
                    {periods.map((item) => (
                        <BillingPeriod
                            periodItem={item}
                            currency={currency}
                            selected={item.period === selectedPeriod}
                            onSelection={() => changeBillingPeriod(item)}
                        />
                    ))}
                </RadioContainer>
            ) : null}
            <Delimiter />
            <PaymentSummaryContainer>
                <SummaryLineContainer>
                    <DueTodayText>{translate("pro.billing_cycle.summary.due_today")}</DueTodayText>
                    <DueTodayText>
                        {sign}
                        {TODAY_PRICE}
                    </DueTodayText>
                </SummaryLineContainer>
            </PaymentSummaryContainer>
        </Wrapper>
    );
};
